
import { Component, Inject, Renderer2, OnInit, Input, DOCUMENT, inject } from '@angular/core';
import { Directionality } from '@angular/cdk/bidi';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { TranslateService } from '@ngx-translate/core';
import { SharedService } from '../../../core/services/shared.service';

type Direction = 'ltr' | 'rtl';

@Component({
    selector: 'lib-language',
    imports: [FlexLayoutModule],
    styles: `
    .lang {
      font-size: 15px;
      margin: 0 5px;
    }

    .lang-icon {
      flex-shrink: 0;
      margin-inline-end: 6px;
      display: block;
    }

    select {
      border: none;
      outline: none;
      cursor: pointer;
      background-color: transparent;
    }

    @media (max-width: 991px) {
      :host {
        svg {
          width: 16px !important;
          height: 16px !important;
        }
      }
    }
  `,
    template: `
    <div
      fxLayout="row"
      fxLayoutAlign="start center"
      class="gap-[2px] flex items-center"
      [style.color]="color"
    >
      <svg
        class="lang-icon"
        width="20"
        height="20"
        viewBox="0 0 13 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M6.5 0C2.91015 0 0 2.91015 0 6.5C0 10.0899 2.91015 13 6.5 13C10.0899 13 13 10.0899 13 6.5C12.9962 2.91172 10.0883 0.00378931 6.5 0ZM4.85187 9H8.14812C7.8125 10.1462 7.25 11.1794 6.5 11.9931C5.75 11.1794 5.1875 10.1462 4.85187 9ZM4.625 8C4.45917 7.00688 4.45917 5.99312 4.625 5H8.375C8.54083 5.99312 8.54083 7.00688 8.375 8H4.625ZM1 6.5C0.999565 5.99277 1.06961 5.48795 1.20812 5H3.61188C3.46271 5.99444 3.46271 7.00556 3.61188 8H1.20812C1.06961 7.51205 0.999565 7.00723 1 6.5ZM8.14812 4H4.85187C5.1875 2.85375 5.75 1.82063 6.5 1.00688C7.25 1.82063 7.8125 2.85375 8.14812 4ZM9.38813 5H11.7919C12.0694 5.98075 12.0694 7.01925 11.7919 8H9.38813C9.53729 7.00556 9.53729 5.99444 9.38813 5ZM11.3981 4H9.18375C8.92859 2.99594 8.50007 2.04415 7.9175 1.1875C9.42255 1.59196 10.6867 2.61343 11.3981 4ZM5.0825 1.1875C4.49993 2.04415 4.07141 2.99594 3.81625 4H1.60188C2.31333 2.61343 3.57745 1.59196 5.0825 1.1875ZM1.60188 9H3.81625C4.07141 10.0041 4.49993 10.9558 5.0825 11.8125C3.57745 11.408 2.31333 10.3866 1.60188 9ZM7.9175 11.8125C8.50007 10.9558 8.92859 10.0041 9.18375 9H11.3981C10.6867 10.3866 9.42255 11.408 7.9175 11.8125Z"
          fill="currentColor"
        />
      </svg>

      <select
        [style.color]="color"
        (change)="onChangeLang($event.target.value)"
        [value]="selectedLanguage"
      >
        <option value="en">English</option>
        <option value="ar">العربية</option>
      </select>
    </div>
  `
})
export class LanguageComponent implements OnInit {
  selectedLanguage!: string;
  /** Text + icon color (e.g. #fff on dark bars, #000 on light forms). */
  @Input() color = '#000';

  private readonly directionality = inject(Directionality);

  constructor(
    private readonly sharedService: SharedService,
    private readonly renderer: Renderer2,
    private readonly translate: TranslateService,
    @Inject(DOCUMENT) private document: Document,
  ) {
    this.selectedLanguage = this.sharedService.getLocalStorage('lang') ?? 'en';
    this.translate.setDefaultLang(this.selectedLanguage);
  }

  ngOnInit(): void {
    this.onChangeDirection();
  }

  onChangeLang(lang: string): void {
    this.selectedLanguage = lang;
    this.sharedService.setLocalStorage('lang', lang);
    this.sharedService.setLocalStorage('dir', lang === 'en' ? 'ltr' : 'rtl');
    this.translate.use(this.selectedLanguage);
    this.onChangeDirection();
  }

  onChangeDirection(): void {
    const dir: Direction = this.selectedLanguage === 'en' ? 'ltr' : 'rtl';

    this.renderer.setAttribute(this.document.documentElement, 'dir', dir);
    this.renderer.setAttribute(this.document.body, 'dir', dir);
    this.document.body.className =
      this.selectedLanguage === 'en' ? 'body-en' : 'body-ar';

    // Keep CDK/Material Directionality in sync so mat-select overlays
    // pick up ltr/rtl immediately without a full page reload.
    this.syncCdkDirectionality(dir);
  }

  private syncCdkDirectionality(dir: Direction): void {
    const directionality = this.directionality as Directionality & {
      valueSignal: { set(value: Direction): void };
    };

    if (directionality.value === dir) {
      return;
    }

    directionality.valueSignal.set(dir);
    directionality.change.emit(dir);
  }
}
